import React from 'react';
import { Case } from "@/types/workflow";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Edit2, FileText, Trash2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { nb } from "date-fns/locale";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

interface CaseDesktopTableProps {
  cases: Case[];
  onEdit: (caseData: Case) => void;
  onDelete: (caseData: Case) => void;
  onViewWorkflow: (caseData: Case) => void;
}

export function CaseDesktopTable({
  cases,
  onEdit,
  onDelete,
  onViewWorkflow,
}: CaseDesktopTableProps) {
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'pending':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'completed':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  return (
    <div className="w-full bg-white/80 dark:bg-card/80 backdrop-blur-sm rounded-lg border shadow-sm">
      <Table>
        <TableHeader className="sticky top-0 z-10 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
          <TableRow>
            <TableHead className="w-[250px]">
              <span className="font-semibold">Tittel</span>
            </TableHead>
            <TableHead className="w-[350px]">
              <span className="font-semibold">Beskrivelse</span>
            </TableHead>
            <TableHead className="w-[150px]">
              <span className="font-semibold">Status</span>
            </TableHead>
            <TableHead className="w-[150px]">
              <span className="font-semibold">Opprettet</span>
            </TableHead>
            <TableHead className="w-[180px] text-right"> {/* Increased width for buttons */}
              <span className="font-semibold">Handlinger</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {cases.map((caseData, index) => (
            <motion.tr
              key={caseData.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="group hover:bg-muted/50 transition-colors"
            >
              <TableCell className="py-4">
                <div className="max-w-[250px]">
                  <span 
                    className="block font-medium truncate cursor-help" 
                    title={caseData.title}
                    role="cell"
                    aria-label={`Tittel: ${caseData.title}`}
                  >
                    {caseData.title}
                  </span>
                </div>
              </TableCell>
              <TableCell className="py-4">
                <div className="max-w-[350px]">
                  <p 
                    className="text-sm text-muted-foreground line-clamp-2 cursor-help" 
                    title={caseData.description}
                    role="cell"
                    aria-label={`Beskrivelse: ${caseData.description}`}
                  >
                    {caseData.description}
                  </p>
                </div>
              </TableCell>
              <TableCell className="py-4">
                <span 
                  className={cn(
                    "px-2 py-1 rounded-full text-xs font-medium inline-block whitespace-nowrap",
                    getStatusColor(caseData.status)
                  )}
                  role="cell"
                  aria-label={`Status: ${caseData.status}`}
                >
                  {caseData.status}
                </span>
              </TableCell>
              <TableCell className="py-4">
                <span 
                  className="text-sm text-muted-foreground whitespace-nowrap"
                  role="cell"
                  aria-label={`Opprettet: ${formatDistanceToNow(new Date(caseData.created_at), {
                    addSuffix: true,
                    locale: nb,
                  })}`}
                >
                  {formatDistanceToNow(new Date(caseData.created_at), {
                    addSuffix: true,
                    locale: nb,
                  })}
                </span>
              </TableCell>
              <TableCell className="py-4">
                <div className="flex items-center justify-end gap-2 pr-6"> {/* Increased right padding and gap */}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onViewWorkflow(caseData)}
                    className="h-8 w-8 hover:bg-primary/10 active:scale-95 transition-all"
                    aria-label="Vis arbeidsflyt"
                  >
                    <FileText className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onEdit(caseData)}
                    className="h-8 w-8 hover:bg-primary/10 active:scale-95 transition-all"
                    aria-label="Rediger sak"
                  >
                    <Edit2 className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onDelete(caseData)}
                    className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10 active:scale-95 transition-all"
                    aria-label="Slett sak"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </motion.tr>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}